import React from "react";
import { Box, Typography, TextField, useTheme, Paper } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/* ---------- helper fns ---------- */
const parenNumRx = /\(\s*\d+\s*\)/g;
const underscoreRx = /_{2,}/g;

// Only clean parenthetical numbers that don't match the questionId
const cleanText = (txt = "", questionId = null) => {
  if (!txt) return "";

  // If there's a questionId, only remove parentheses that don't match it
  if (questionId != null) {
    const expectedPattern = new RegExp(`\\(\\s*${questionId}\\s*\\)`, "g");
    // Keep the expected pattern, remove others
    return txt
      .replace(/\(\s*\d+\s*\)/g, (match) => {
        return match.match(expectedPattern) ? match : "";
      })
      .trim();
  }

  // If no questionId, remove all parenthetical numbers
  return txt.replace(parenNumRx, "").trim();
};

const splitForBlanks = (str, hasBlank) => {
  if (underscoreRx.test(str)) {
    underscoreRx.lastIndex = 0;
    const parts = str.split(underscoreRx);
    return parts.map((p, i) => ({ text: p, blank: i < parts.length - 1 }));
  }
  return [{ text: str, blank: hasBlank }];
};

const measureBlank = (raw) => {
  const m = raw.match(/_{2,}/);
  if (!m) return 160;
  const len = m[0].length;
  return Math.min(Math.max(len * 8, 120), 280); // clamp
};

/* ---------- component ---------- */
export default function MockNoteCompletion({
  noteCompletionData,
  handleAnswerChange,
  answers,
  instructions = "",
  questionMainHeading = "",
}) {
  const theme = useTheme();
  const maroon = "#9D2235"; // used for heading + numbers
  const divider = theme.palette.divider;
  const bgCard = theme.palette.background.paper;
  const txtMain = theme.palette.text.primary;

  const sections = noteCompletionData?.sections || [];
  return (
    <Box
      sx={{
        width: "100%",
        userSelect: "text", // Enable text selection
        "& *": { userSelect: "text" }, // Ensure all children are selectable
      }}
    >
      {/* Display instructions if provided - matching MCQ design */}
      {instructions && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            border: `1px solid ${divider}`,
            borderRadius: 1.5,
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 28, color: maroon, mr: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: maroon }}>
              Instruction
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: txtMain,
              lineHeight: 1.5,
              whiteSpace: "pre-line",
              userSelect: "text",
              cursor: "text",
            }}
          >
            {instructions}
          </Typography>
        </Paper>
      )}

      {/* Display questionMainHeading if provided */}
      {questionMainHeading && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: `1px solid ${divider}`,
            borderRadius: 2,
            bgcolor: bgCard,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: maroon,
              textAlign: "center",
              userSelect: "text",
              cursor: "text",
            }}
          >
            {questionMainHeading}
          </Typography>
        </Box>
      )}

      {sections.map((section, sIdx) => (
        <Box
          key={sIdx}
          sx={{
            my: 1,
            p: 3,
            border: `1px solid ${divider}`,
            borderRadius: 2,
            bgcolor: bgCard,
            userSelect: "text", // Enable text selection on container
            "& *": { userSelect: "text" }, // Ensure all children are selectable
          }}
        >
          {/* ----- section heading w/ underline ----- */}
          {section.heading && (
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
                pb: 0.5,
                color: txtMain,
                borderBottom: `1px solid ${divider}`,
                userSelect: "text", // Enable text selection
                cursor: "text", // Show text cursor
              }}
            >
              {section.heading}
            </Typography>
          )}

          {/* ----- notes grouped by questionHeading ----- */}
          {(() => {
            // Group notes by their heading (questionHeading / left column label)
            // Fallback to questionMainHeading when questionHeading is missing
            // Support both `section.notes` and `section.questions` (API uses
            // `questions` in listeningTest payloads).
            const grouped = {};
            const notesArray = section.notes || section.questions || [];
            notesArray.forEach((note) => {
              const key = (
                note?.heading ||
                note?.questionMainHeading ||
                ""
              ).trim();
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(note);
            });

            return Object.entries(grouped).map(
              ([groupHeading, groupNotes], gIdx) => {
                const groupRange =
                  groupNotes?.[0]?.questionRange ||
                  section.sectionQuestionRange ||
                  "";
                const groupInstr = groupNotes?.[0]?.instructions || "";
                return (
                  <Box
                    key={gIdx}
                    sx={{
                      // the UI shows inline blanks without an enclosing box.
                      border: "none",
                      borderRadius: 2,
                      bgcolor: bgCard,
                    }}
                  >
                    {/* Group heading (renders the questionHeading on left in screenshots)
                    Only render when a real heading exists to avoid adding a
                    blank line (previously a non-breaking space produced the gap).
                */}
                    {groupHeading ? (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 0.5, color: txtMain }}
                        >
                          {groupHeading}
                        </Typography>
                        {groupRange ? (
                          <Typography
                            variant="body2"
                            sx={{ color: txtMain, mb: 0.5 }}
                          >
                            {groupRange}
                          </Typography>
                        ) : null}
                        {groupInstr ? (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: txtMain, mb: 1 }}
                          >
                            {groupInstr}
                          </Typography>
                        ) : null}
                      </>
                    ) : null}

                    {/* Render each note in the group (plain text or input) */}
                    {groupNotes.map((note, nIdx) => {
                      const qid = note?.questionId;
                      const rawTxt = note?.text ?? "";
                      const dispTxt = cleanText(rawTxt, qid);
                      const hasValidQuestionId =
                        Number.isInteger(qid) && qid > 0;

                      if (!hasValidQuestionId) {
                        return (
                          <Typography
                            key={nIdx}
                            component="div"
                            variant="body1"
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              lineHeight: 1.8,
                              mb: 1.5,
                              userSelect: "text",
                            }}
                          >
                            {dispTxt}
                          </Typography>
                        );
                      }

                      const segments = splitForBlanks(dispTxt, true);
                      const blankW = measureBlank(rawTxt);

                      return (
                        <Typography
                          key={nIdx}
                          component="div"
                          variant="body1"
                          data-question-id={qid}
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            lineHeight: 1.8,
                            mb: 1.5,
                            userSelect: "text",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ mr: 1, fontWeight: 700, color: maroon }}
                          >
                            ({qid})
                          </Box>
                          {segments.map((seg, i) => (
                            <Box
                              key={i}
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {seg.text}
                              {seg.blank && (
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  value={answers?.[qid] || ""}
                                  onChange={(e) =>
                                    handleAnswerChange?.(qid, e.target.value)
                                  }
                                  autoComplete="off"
                                  sx={{
                                    mx: 1,
                                    minWidth: blankW,
                                    maxWidth: blankW,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 3,
                                      fontWeight: 600,
                                      px: 1,
                                      "& fieldset": { borderColor: divider },
                                      "&:hover fieldset": {
                                        borderColor: maroon,
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: maroon,
                                      },
                                    },
                                    "& input": {
                                      textAlign: "center",
                                      color: maroon,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          ))}
                        </Typography>
                      );
                    })}
                  </Box>
                );
              }
            );
          })()}
        </Box>
      ))}
    </Box>
  );
}
